import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useNavigate } from "react-router-dom";
import {
  fetchProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminUploadImage,
  adminListContacts,
  adminUpdateContactStatus,
  adminDeleteContact,
  adminPatchProduct,
  adminReorderProducts,
  resolveImageUrl,
} from "../lib/api";
import { GripVertical } from "lucide-react";
import Logo from "../components/Logo";
import SettingsPanel from "./SettingsPanel";
import ProductImagesEditor from "../components/ProductImagesEditor";
import { toast } from "sonner";

const empty = {
  code: "",
  name: "",
  category: "",
  year: "",
  materials: "",
  dimensions: "",
  description: "",
  story: "",
  image: "",
  images: [],
  span: "default",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("tutti");
  const [tab, setTab] = useState("collezione");
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const CATEGORY_SUGGESTIONS = [
    "Seduta",
    "Sedia",
    "Poltrona",
    "Sgabello",
    "Tavolo",
    "Composizione",
    "Interno",
    "Lampada",
    "Contenitore",
    "Specchio",
  ];

  const onInlineCategoryChange = async (p, newCat) => {
    if (!newCat || newCat === p.category) return;
    setItems((prev) => prev.map((it) => (it.id === p.id ? { ...it, category: newCat } : it)));
    try {
      await adminPatchProduct(p.id, { category: newCat });
      toast.success("Categoria aggiornata");
    } catch {
      toast.error("Errore aggiornamento categoria");
      refresh();
    }
  };

  const onReorderDrop = async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const arr = [...items];
    const fromIdx = arr.findIndex((p) => p.id === fromId);
    const toIdx = arr.findIndex((p) => p.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    setItems(arr);
    try {
      await adminReorderProducts(arr.map((p) => p.id));
      toast.success("Ordine salvato");
    } catch {
      toast.error("Errore riordino");
      refresh();
    }
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [loading, user, nav]);

  const refresh = () => fetchProducts().then(setItems);
  const refreshMessages = () => adminListContacts().then(setMessages).catch(() => {});
  useEffect(() => {
    if (user) {
      refresh();
      refreshMessages();
    }
  }, [user]);

  const scrollToForm = () => {
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="admin-form"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(empty);
    scrollToForm();
  };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      materials: (p.materials || []).join(", "),
      images: p.images && p.images.length ? p.images : (p.image ? [p.image] : []),
    });
    scrollToForm();
  };
  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const { url } = await adminUploadImage(f);
      setForm((s) => ({ ...s, image: url }));
      toast.success("Immagine caricata");
    } catch {
      toast.error("Upload fallito");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      ...form,
      materials:
        typeof form.materials === "string"
          ? form.materials.split(",").map((s) => s.trim()).filter(Boolean)
          : form.materials,
      images: form.images || [],
      image: (form.images && form.images[0]) || form.image || "",
    };
    try {
      if (editing === "new") await adminCreateProduct(payload);
      else await adminUpdateProduct(editing, payload);
      toast.success("Salvato");
      cancel();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore di salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo pezzo?")) return;
    await adminDeleteProduct(id);
    toast.success("Eliminato");
    refresh();
  };

  const changeStatus = async (id, status) => {
    try {
      await adminUpdateContactStatus(id, status);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch {
      toast.error("Errore aggiornamento stato");
    }
  };

  const removeMessage = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo messaggio?")) return;
    try {
      await adminDeleteContact(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Messaggio eliminato");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const STATUS_META = {
    nuovo: { label: "Nuovo", color: "#9E8B65" },
    da_contattare: { label: "Da contattare", color: "#B45309" },
    letto: { label: "Letto", color: "#5C5A56" },
    archiviato: { label: "Archiviato", color: "#A8A39A" },
  };
  const STATUS_ORDER = ["nuovo", "da_contattare", "letto", "archiviato"];

  const visibleMessages =
    statusFilter === "tutti"
      ? messages
      : messages.filter((m) => (m.status || "nuovo") === statusFilter);
  const newCount = messages.filter((m) => (m.status || "nuovo") === "nuovo").length;

  if (loading || !user) return null;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen px-6 sm:px-12 py-10">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between mb-12">
          <Logo label={settings.wordmark || "tramæ"} onClick={() => nav("/")} dataTestid="admin-logo" />
          <div className="flex items-center gap-6">
            <span className="label-eyebrow">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                nav("/admin/login");
              }}
              className="tr-btn-ghost"
            >
              Esci
            </button>
          </div>
        </header>

        <div className="flex items-end justify-between mb-8 flex-wrap gap-6">
          <div className="flex items-center gap-8">
            <button
              data-testid="admin-tab-collezione"
              onClick={() => setTab("collezione")}
              className={`font-display text-4xl sm:text-5xl font-light transition-opacity ${
                tab === "collezione" ? "opacity-100" : "opacity-30"
              }`}
            >
              Collezione
            </button>
            <button
              data-testid="admin-tab-pagina"
              onClick={() => setTab("pagina")}
              className={`font-display text-4xl sm:text-5xl font-light transition-opacity ${
                tab === "pagina" ? "opacity-100" : "opacity-30"
              }`}
            >
              Pagina
            </button>
            <button
              data-testid="admin-tab-messaggi"
              onClick={() => {
                setTab("messaggi");
                refreshMessages();
              }}
              className={`font-display text-4xl sm:text-5xl font-light transition-opacity ${
                tab === "messaggi" ? "opacity-100" : "opacity-30"
              }`}
            >
              Messaggi
              {newCount > 0 && (
                <span className="ml-3 align-top text-sm font-sub tracking-[0.2em]" style={{ color: "#9E8B65" }}>
                  {newCount}
                </span>
              )}
            </button>
          </div>
          {tab === "collezione" && (
            <button data-testid="admin-new" onClick={startNew} className="tr-btn">
              + Nuovo pezzo
            </button>
          )}
        </div>

        {tab === "pagina" && <SettingsPanel />}

        {tab === "collezione" && editing && (
          <div
            data-testid="admin-form"
            className="border border-[color:var(--tr-border)] p-8 mb-12 bg-[color:var(--tr-bg-2)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                ["code", "Codice (es. 07)"],
                ["name", "Nome"],
                ["category", "Categoria"],
                ["year", "Anno (es. MMXXIV)"],
                ["dimensions", "Dimensioni"],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <span className="label-eyebrow">{label}</span>
                  <input
                    data-testid={`admin-field-${k}`}
                    value={form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="tr-field !py-2"
                  />
                </label>
              ))}
              <label className="block">
                <span className="label-eyebrow">Layout</span>
                <select
                  data-testid="admin-field-span"
                  value={form.span || "default"}
                  onChange={(e) => setForm({ ...form, span: e.target.value })}
                  className="tr-field !py-2"
                >
                  <option value="default">Default</option>
                  <option value="tall">Alta</option>
                  <option value="wide">Larga</option>
                </select>
              </label>
            </div>
            <label className="block mt-6">
              <span className="label-eyebrow">Materiali (separati da virgola)</span>
              <input
                data-testid="admin-field-materials"
                value={form.materials || ""}
                onChange={(e) => setForm({ ...form, materials: e.target.value })}
                className="tr-field !py-2"
              />
            </label>
            <label className="block mt-6">
              <span className="label-eyebrow">Descrizione breve</span>
              <input
                data-testid="admin-field-description"
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="tr-field !py-2"
              />
            </label>
            <label className="block mt-6">
              <span className="label-eyebrow">Storia</span>
              <textarea
                data-testid="admin-field-story"
                value={form.story || ""}
                onChange={(e) => setForm({ ...form, story: e.target.value })}
                rows={4}
                className="tr-field !py-2 resize-none"
              />
            </label>
            <div className="mt-8">
              <ProductImagesEditor
                images={form.images}
                onChange={(next) => setForm((s) => ({ ...s, images: next, image: next[0] || "" }))}
              />
            </div>
            <div className="flex items-center gap-6 mt-10">
              <button data-testid="admin-save" onClick={save} disabled={busy} className="tr-btn">
                {busy ? "Salvo…" : "Salva"}
              </button>
              <button onClick={cancel} className="tr-btn-ghost">
                Annulla
              </button>
            </div>
          </div>
        )}

        {tab === "collezione" && (
          <>
            <p className="font-body text-[color:var(--tr-ink-soft)] text-sm mb-4">
              Trascina la maniglia ⠿ per riordinare. Cambia la categoria direttamente dal menu sulla scheda.
            </p>
            <div
              data-testid="admin-collection-list"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {items.map((p, idx) => (
                <div
                  key={p.id}
                  data-testid={`admin-item-${p.code}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", p.id);
                    setDragId(p.id);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverId !== p.id) setDragOverId(p.id);
                  }}
                  onDragLeave={() => {
                    if (dragOverId === p.id) setDragOverId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromId = e.dataTransfer.getData("text/plain") || dragId;
                    onReorderDrop(fromId, p.id);
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  className={`border bg-[color:var(--tr-bg)] transition-all ${
                    dragId === p.id
                      ? "opacity-40"
                      : dragOverId === p.id
                      ? "border-[color:var(--tr-ink)] ring-2 ring-[color:var(--tr-ink)]/20"
                      : "border-[color:var(--tr-border)]"
                  }`}
                >
                  <div className="relative aspect-[4/3] bg-[color:var(--tr-bg-2)]">
                    {p.image && (
                      <img
                        src={resolveImageUrl(p.image)}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <span
                      className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-[color:var(--tr-bg)]/90 backdrop-blur-sm cursor-grab active:cursor-grabbing font-sub text-[0.65rem] uppercase tracking-[0.22em]"
                      title="Trascina per riordinare"
                    >
                      <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {idx + 1}
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <p className="label-eyebrow">N° {p.code}</p>
                      <h3 className="font-display text-xl mt-1">{p.name}</h3>
                    </div>
                    <label className="block">
                      <span className="label-eyebrow">Categoria</span>
                      <select
                        data-testid={`admin-category-${p.code}`}
                        value={p.category || ""}
                        onChange={(e) => onInlineCategoryChange(p, e.target.value)}
                        className="tr-field !py-1.5 !text-sm"
                      >
                        {[...new Set([p.category, ...CATEGORY_SUGGESTIONS])].filter(Boolean).map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="flex gap-3 pt-1">
                      <button
                        data-testid={`admin-edit-${p.code}`}
                        onClick={() => startEdit(p)}
                        className="text-[0.72rem] uppercase tracking-[0.22em] font-sub border-b border-[color:var(--tr-ink)] pb-0.5"
                      >
                        Modifica
                      </button>
                      <button
                        data-testid={`admin-delete-${p.code}`}
                        onClick={() => remove(p.id)}
                        className="text-[0.72rem] uppercase tracking-[0.22em] font-sub text-red-700 border-b border-red-700 pb-0.5"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "messaggi" && (
          <div data-testid="admin-messages" className="space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
              {["tutti", ...STATUS_ORDER].map((s) => {
                const active = statusFilter === s;
                const count =
                  s === "tutti"
                    ? messages.length
                    : messages.filter((m) => (m.status || "nuovo") === s).length;
                return (
                  <button
                    key={s}
                    data-testid={`admin-filter-${s}`}
                    onClick={() => setStatusFilter(s)}
                    className={`px-4 py-2 font-sub text-[0.72rem] uppercase tracking-[0.22em] border transition-colors ${
                      active
                        ? "bg-[color:var(--tr-ink)] text-[color:var(--tr-bg)] border-[color:var(--tr-ink)]"
                        : "border-[color:var(--tr-border)] hover:border-[color:var(--tr-ink)]"
                    }`}
                  >
                    {s === "tutti" ? "Tutti" : STATUS_META[s].label}
                    <span className="ml-2 opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>

            {visibleMessages.length === 0 && (
              <p className="font-body text-[color:var(--tr-ink-soft)]">
                Nessun messaggio in questa categoria.
              </p>
            )}
            {visibleMessages.map((m) => {
              const current = m.status || "nuovo";
              const meta = STATUS_META[current];
              return (
                <article
                  key={m.id}
                  data-testid={`admin-message-${m.id}`}
                  className="border border-[color:var(--tr-border)] bg-[color:var(--tr-bg)] p-6"
                >
                  <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-display text-2xl leading-tight">{m.name}</p>
                        <span
                          data-testid={`admin-message-status-${m.id}`}
                          className="inline-flex items-center gap-2 px-2 py-1 text-[0.65rem] uppercase tracking-[0.22em] font-sub border"
                          style={{ color: meta.color, borderColor: meta.color }}
                        >
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: meta.color }}
                          />
                          {meta.label}
                        </span>
                      </div>
                      <a
                        href={`mailto:${m.email}`}
                        className="font-sub text-[0.78rem] uppercase tracking-[0.22em] hover:text-[color:var(--tr-accent)]"
                      >
                        {m.email}
                      </a>
                      {m.phone && (
                        <span className="ml-4 font-sub text-[0.78rem] uppercase tracking-[0.22em] text-[color:var(--tr-ink-soft)]">
                          · {m.phone}
                        </span>
                      )}
                    </div>
                    <span className="label-eyebrow">
                      {m.created_at ? new Date(m.created_at).toLocaleString("it-IT") : ""}
                    </span>
                  </div>
                  <p className="font-body leading-relaxed whitespace-pre-wrap mb-5">{m.message}</p>

                  <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-[color:var(--tr-border)]">
                    <span className="label-eyebrow mr-2">Stato →</span>
                    {STATUS_ORDER.map((s) => {
                      const active = current === s;
                      return (
                        <button
                          key={s}
                          data-testid={`admin-message-${m.id}-set-${s}`}
                          onClick={() => changeStatus(m.id, s)}
                          disabled={active}
                          className={`px-3 py-1.5 font-sub text-[0.68rem] uppercase tracking-[0.22em] border transition-colors ${
                            active
                              ? "bg-[color:var(--tr-ink)] text-[color:var(--tr-bg)] border-[color:var(--tr-ink)] cursor-default"
                              : "border-[color:var(--tr-border)] hover:border-[color:var(--tr-ink)]"
                          }`}
                        >
                          {STATUS_META[s].label}
                        </button>
                      );
                    })}
                    <button
                      data-testid={`admin-message-${m.id}-delete`}
                      onClick={() => removeMessage(m.id)}
                      className="ml-auto font-sub text-[0.68rem] uppercase tracking-[0.22em] text-red-700 border-b border-red-700 pb-0.5"
                    >
                      Elimina
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
